import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import Drawer from '../../../components/ui/Drawer';
import TabBar from '../../../components/TabBar';
import { useDrawerResize } from '../../../hooks/useDrawerResize';
import NotesPanel from '../../../components/common/NotesPanel';
import { useResolvedAccessLevel } from '../../../components/PermissionsContainer';
import { PermissionAwareTextarea } from '../../../contexts/PermissionAccessContext';
import safetySystemService from '../../../services/safetySystem.service';
import { emptyIncidentLookups } from '../../../types/safetySystem';
import type { IncidentDetail, IncidentLookups, IncidentSaveBody, IncidentSaveResponse } from '../../../types/safetySystem';
import SafetyDocuments from './SafetyDocuments';
import IncidentInfoTab, { NoshowReasons } from './IncidentInfoTab';
import { ClaimsTab, CostsTab, EquipmentTab, INCIDENT_HISTORY_LABELS, PartiesTab, TicketsTab } from './IncidentRowsTabs';
import { DiscardFooter, ResizeHandle, SafetyHistoryTab, errorText } from './safetyDrawerShared';
import { diffChanges, isDirty } from './unitForm';
import { inputClass, str } from './unitFields';
import { ChildRow, childRowsForSave, childrenDirty, withKeys } from './ticketForm';
import {
  CLAIM_FIELDS, COST_FIELDS, EQUIPMENT_FIELDS, IncidentTabId, PARTY_FIELDS, alcoholTestPrompt, createIncidentBody, dateSet, drugTestPrompt,
  newClaimRow, newCostRow, newEquipmentRow, newIncidentDefaults, newPartyRow, pickIncidentFields,
} from './incidentForm';

/**
 * Incident drawer — port of the V1 Safety incident page. Tabs: Incident, Inv Parties,
 * Equip & Cargo, Tickets (saved incidents only), Claims, Costs, Documents, Notes, History.
 * `recno === null` is a brand-new incident: the first save creates it and the drawer stays
 * open on the created recno so the record-bound tabs become available.
 *
 * Save posts `{ incident: <changed keys>, parties, equipment, claims, costs, noshow }`
 * (ticketForm.childRowsForSave for the children); a create posts the form less its still-
 * blank keys (incidentForm.createIncidentBody). A 422 `{failures}` renders as the
 * "Incident cannot be closed" checklist; a string 422 shows inline; a 409 lists the
 * duplicate GWI claim numbers (no bypass); `warnings` on a 200 render amber and keep the
 * drawer open. Delete posts `incident.deleted:'Y'` + `deletecomments` from an inline reason.
 */

export interface IncidentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  recno: number | null;
  onSaved: () => void;
  initialTab?: IncidentTabId;
}

const SAVE_TABS: IncidentTabId[] = ['incident', 'parties', 'equipment', 'claims', 'costs'];
const EMPTY_NOSHOW: NoshowReasons = { drug: '', alcohol: '', alcohollate: '' };

/** Lookups from the last loaded incident, so a New Incident drawer has them too. */
let lookupsCache: IncidentLookups | null = null;

interface Dupe { recno: number; gwiclaimnum: string; incidentdatetime: string | null; incidentstatus: string | null }

const newDetail = (): IncidentDetail => ({
  incident: { ...newIncidentDefaults(), incidentstatus: 'OPEN' },
  parties: [], equipment: [], claims: [], costs: [], costlines: [], tickets: [],
  summary: { costs: 0, reimbursements: 0, chargeback: 0, out_of_pocket: 0, injuries: 0, fatalities: 0, towed: 0 },
  lookups: lookupsCache ?? emptyIncidentLookups(),
});

export default function IncidentDrawer({ isOpen, onClose, recno: recnoProp, onSaved, initialTab }: IncidentDrawerProps) {
  const canEdit = useResolvedAccessLevel('safety-system-unit-edit', true) >= 2;
  const { width, onHandleMouseDown, onHandleDoubleClick } = useDrawerResize({
    isOpen, storageKey: 'safetyIncidentDrawerWidth', minWidth: 520, defaultWidth: 1280,
  });
  const { accounts } = useMsal();
  const currentUser = accounts[0]?.username || accounts[0]?.name || '';

  // The recno the drawer is showing: the prop, or the recno a create just produced.
  const [recno, setRecno] = useState<number | null>(recnoProp);
  useEffect(() => { setRecno(recnoProp); }, [recnoProp, isOpen]);

  const [detail, setDetail] = useState<IncidentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState<IncidentTabId>('incident');
  const [noteCount, setNoteCount] = useState(0);

  // Editable copies + the pristine originals (in `detail`) they are diffed against on Save.
  const [incident, setIncident] = useState<Record<string, any>>({});
  const [noshow, setNoshow] = useState<NoshowReasons>(EMPTY_NOSHOW);
  const [parties, setParties] = useState<ChildRow[]>([]);
  const [equipment, setEquipment] = useState<ChildRow[]>([]);
  const [claims, setClaims] = useState<ChildRow[]>([]);
  const [costs, setCosts] = useState<ChildRow[]>([]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [failures, setFailures] = useState<string[]>([]);
  const [dupes, setDupes] = useState<Dupe[] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Inline delete-reason prompt.
  const [deletePrompt, setDeletePrompt] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  const contentRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (isOpen && contentRef.current) setPortalContainer(contentRef.current.closest('dialog') || null);
  }, [isOpen, detail]);

  const resetForm = useCallback((d: IncidentDetail | null) => {
    setIncident(d ? pickIncidentFields(d.incident) : {});
    setNoshow(EMPTY_NOSHOW);
    setParties(d ? withKeys(d.parties ?? []) : []);
    setEquipment(d ? withKeys(d.equipment ?? []) : []);
    setClaims(d ? withKeys(d.claims ?? []) : []);
    setCosts(d ? withKeys(d.costs ?? []) : []);
    setSaveError('');
    setFailures([]);
    setDupes(null);
    setConfirmDiscard(false);
    setDeletePrompt(false);
    setDeleteReason('');
  }, []);

  const load = useCallback((id: number, signal?: AbortSignal) => {
    setLoading(true);
    setLoadError('');
    return safetySystemService.getIncident(id, signal)
      .then((d) => {
        if (signal?.aborted) return;
        lookupsCache = d.lookups ?? lookupsCache;
        setDetail(d);
        resetForm(d);
      })
      .catch((e) => {
        if (signal?.aborted) return;
        setDetail(null);
        setLoadError(e?.response?.data?.detail || 'Unable to load this incident.');
      })
      .finally(() => { if (!signal?.aborted) setLoading(false); });
  }, [resetForm]);

  /** Re-read the record without touching the edited copies (after link / unlink / cost lines). */
  const refresh = useCallback(async () => {
    if (recno === null) return;
    try {
      const d = await safetySystemService.getIncident(recno);
      setDetail(d);
      // Costs / claims totals come from the server; keep the user's row edits but refresh saved rows' totals.
      setCosts((p) => p.map((r) => {
        const fresh = d.costs.find((c) => Number(c.recno) === Number(r.recno));
        return fresh ? { ...r, totalaccost: fresh.totalaccost } : r;
      }));
      setClaims((p) => p.map((r) => {
        const fresh = d.claims.find((c) => Number(c.recno) === Number(r.recno));
        return fresh ? { ...r, claimtotalcosts: fresh.claimtotalcosts } : r;
      }));
      onSaved();
    } catch (e: any) {
      setLoadError(e?.response?.data?.detail || 'Unable to reload this incident.');
    }
  }, [recno, onSaved]);

  // The tab / notices reset when the drawer opens or is pointed at another incident by its
  // owner -- NOT when a create switches `recno` to the new record, so the user stays on
  // the tab they created from and keeps the create's warnings.
  useEffect(() => {
    setActiveTab(initialTab ?? 'incident');
    setNoteCount(0);
    setWarnings([]);
  }, [isOpen, recnoProp, initialTab]);

  // Load on open / recno change. A null recno is a new incident: no fetch, blank form.
  useEffect(() => {
    if (!isOpen) {
      setDetail(null);
      resetForm(null);
      return;
    }
    if (recno === null) {
      const d = newDetail();
      setDetail(d);
      resetForm(d);
      setLoading(false);
      setLoadError('');
      return;
    }
    const controller = new AbortController();
    load(recno, controller.signal);
    return () => controller.abort();
  }, [isOpen, recno, load, resetForm]);

  const isNew = recno === null;
  const baseIncident = useMemo(() => (detail ? pickIncidentFields(detail.incident) : {}), [detail]);
  const isDeleted = str(detail?.incident?.deleted).toUpperCase() === 'Y';

  const dirty = useMemo(() => {
    if (!detail) return false;
    return isDirty(baseIncident, incident)
      || childrenDirty(detail.parties ?? [], parties, PARTY_FIELDS)
      || childrenDirty(detail.equipment ?? [], equipment, EQUIPMENT_FIELDS)
      || childrenDirty(detail.claims ?? [], claims, CLAIM_FIELDS)
      || childrenDirty(detail.costs ?? [], costs, COST_FIELDS)
      || Object.values(noshow).some((v) => v.trim());
  }, [detail, baseIncident, incident, parties, equipment, claims, costs, noshow]);

  const clearErrors = () => { setSaveError(''); setFailures([]); setDupes(null); };
  const onIncidentChange = (key: string, value: unknown) => { setIncident((p) => ({ ...p, [key]: value })); clearErrors(); };

  /** The reason textareas only post when their timing prompt is showing (a later date edit may hide one). */
  const activeNoshow = (): NonNullable<IncidentSaveBody['noshow']> => {
    const out: NonNullable<IncidentSaveBody['noshow']> = {};
    if (drugTestPrompt(incident) === 'noshow' && noshow.drug.trim()) out.drug = noshow.drug.trim();
    const a = alcoholTestPrompt(incident);
    if (a === 'noshow' && noshow.alcohol.trim()) out.alcohol = noshow.alcohol.trim();
    if (a === 'late' && noshow.alcohollate.trim()) out.alcohollate = noshow.alcohollate.trim();
    return out;
  };

  const buildBody = (): IncidentSaveBody => {
    // A create drops the keys still blank (the server baseline already holds them: posting
    // '' against 0000-00-00 only logged noise history); an edit posts the diff.
    const inc = isNew ? createIncidentBody(incident) : diffChanges(baseIncident, incident);
    const ns = activeNoshow();
    // The reasons also live on the incident columns (V1 wrote both the column and the note).
    if (ns.drug) inc.drugnoshowreason = ns.drug;
    if (ns.alcohol) inc.alcoholnoshowreason = ns.alcohol;
    if (ns.alcohollate) inc.alcohollatereason = ns.alcohollate;
    return {
      incident: inc,
      parties: childRowsForSave(detail?.parties ?? [], parties, PARTY_FIELDS),
      equipment: childRowsForSave(detail?.equipment ?? [], equipment, EQUIPMENT_FIELDS),
      claims: childRowsForSave(detail?.claims ?? [], claims, CLAIM_FIELDS),
      costs: childRowsForSave(detail?.costs ?? [], costs, COST_FIELDS),
      noshow: ns,
    };
  };

  const applyError = (e: any) => {
    const status = e?.response?.status;
    const d = e?.response?.data?.detail;
    if (status === 409 && d && Array.isArray(d.dupes)) setDupes(d.dupes as Dupe[]);
    if (status === 422 && d && Array.isArray(d.failures)) setFailures(d.failures as string[]);
    setSaveError(errorText(e));
  };

  const handleSave = async () => {
    if (!detail || !SAVE_TABS.includes(activeTab)) return;
    if (!isNew && !dirty) { onClose(); return; }
    const body = buildBody();
    setSaving(true);
    clearErrors();
    setWarnings([]);
    try {
      let res: IncidentSaveResponse;
      if (isNew) res = await safetySystemService.createIncident(body);
      else res = await safetySystemService.saveIncident(recno as number, body);
      const warn = res?.warnings ?? [];
      setWarnings(warn);
      onSaved();
      if (isNew) {
        // Stay open on the created incident so Tickets / Documents / Notes / History become available.
        setRecno(res.recno);
      } else if (warn.length) {
        await load(recno as number);
      } else {
        onClose();
      }
    } catch (e: any) {
      applyError(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (recno === null) return;
    const text = deleteReason.trim();
    if (!text) return;
    setSaving(true);
    clearErrors();
    try {
      await safetySystemService.saveIncident(recno, { incident: { deleted: 'Y' }, deletecomments: text });
      onSaved();
      onClose();
    } catch (e: any) {
      applyError(e);
    } finally {
      setSaving(false);
    }
  };

  const requestClose = (): boolean => {
    if (dirty) { setConfirmDiscard(true); return false; }
    return true;
  };
  const handleCancel = () => { if (requestClose()) onClose(); };
  const handleDiscard = () => { resetForm(detail); onClose(); };

  const typeName = (() => {
    const id = str(incident.incidenttype);
    const hit = (detail?.lookups?.incidenttypes ?? []).find((t) => String(t.recno) === id);
    return hit ? str(hit.label) : str(detail?.incident?.incidenttypename);
  })();
  const status = isNew ? 'NEW' : str(detail?.incident?.incidentstatus).toUpperCase();
  const title = detail ? (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-lg font-semibold">
        {[`Incident# ${isNew ? 'NEW' : recno}`, typeName, status].filter(Boolean).join(' · ')}
      </span>
      {!isNew && status && (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${status === 'CLOSED' ? 'bg-gray-200 text-heading dark:bg-gray-700' : 'bg-accent text-accent-fg'}`}>{status}</span>
      )}
      {isDeleted && <span className="inline-flex items-center rounded-full bg-danger px-2.5 py-0.5 text-xs font-semibold text-danger-fg">DELETED</span>}
    </div>
  ) : (loading ? 'Loading incident…' : 'Incident');

  const liveCount = (rows: ChildRow[]) => rows.filter((r) => str(r.deleted).toUpperCase() !== 'Y').length;
  const badge = (n: number) => (n > 0 ? { badge: n } : {});
  const tabs = [
    { id: 'incident', label: 'Incident', current: activeTab === 'incident' },
    { id: 'parties', label: 'Inv Parties', current: activeTab === 'parties', ...badge(liveCount(parties)) },
    { id: 'equipment', label: 'Equip & Cargo', current: activeTab === 'equipment', ...badge(liveCount(equipment)) },
    { id: 'tickets', label: 'Tickets', current: activeTab === 'tickets', disabled: isNew, ...badge(detail?.tickets?.length ?? 0) },
    { id: 'claims', label: 'Claims', current: activeTab === 'claims', ...badge(liveCount(claims)) },
    { id: 'costs', label: 'Costs', current: activeTab === 'costs', ...badge(liveCount(costs)) },
    { id: 'documents', label: 'Documents', current: activeTab === 'documents', disabled: isNew },
    { id: 'notes', label: 'Notes', current: activeTab === 'notes', disabled: isNew, ...badge(noteCount) },
    { id: 'history', label: 'History', current: activeTab === 'history', disabled: isNew },
  ];

  const editable = canEdit && !isDeleted;

  const footer = confirmDiscard ? (
    <DiscardFooter onDiscard={handleDiscard} onKeep={() => setConfirmDiscard(false)} />
  ) : (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex gap-2">
        {editable && detail && !isNew && SAVE_TABS.includes(activeTab) && (
          <button type="button" onClick={() => { setDeletePrompt(true); setDeleteReason(''); }} disabled={saving}
            className="rounded-md border border-rule px-3 py-1.5 text-sm text-danger-fg hover:bg-highlight disabled:cursor-not-allowed">
            Delete Incident
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={handleCancel} className="rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight">Cancel</button>
        {editable && detail && SAVE_TABS.includes(activeTab) && (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
          >
            {saving ? 'Saving…' : isNew ? 'Create Incident' : dateSet(incident.incidentclosedate) && !dateSet(baseIncident.incidentclosedate) ? 'Save & Close Incident' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      onBeforeClose={requestClose}
      title={title}
      maxWidth="full"
      width={`${width}px`}
      contentClassName="relative flex-1 overflow-y-auto px-4 py-4 sm:px-6"
      footer={footer}
    >
      <ResizeHandle width={width} onHandleMouseDown={onHandleMouseDown} onHandleDoubleClick={onHandleDoubleClick} />
      <div ref={contentRef}>
        <TabBar tabs={tabs} onTabChange={(id) => setActiveTab(id as IncidentTabId)} compact noBorder className="mb-4" />

        {loadError && <div role="alert" className="mb-3 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{loadError}</div>}
        {saveError && (
          <div role="alert" className="mb-3 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">
            <div className={failures.length ? 'font-medium' : ''}>{saveError}</div>
            {failures.length > 0 && (
              <ul aria-label="Closing checklist" className="mt-1 list-disc pl-5">
                {failures.map((f) => <li key={f}>{f}</li>)}
              </ul>
            )}
            {dupes && dupes.length > 0 && (
              <ul className="mt-1 list-disc pl-5">
                {dupes.map((d) => <li key={d.recno}>{`Incident #${d.recno} — ${d.gwiclaimnum}${d.incidentdatetime ? ` (${d.incidentdatetime})` : ''}${d.incidentstatus ? ` ${d.incidentstatus}` : ''}`}</li>)}
              </ul>
            )}
          </div>
        )}
        {warnings.length > 0 && (
          <div role="status" className="mb-3 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-100">
            <div className="font-medium">Saved with warnings</div>
            <ul className="list-disc pl-5">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
          </div>
        )}

        {deletePrompt && !loading && detail && (
          <div role="group" aria-label="Delete incident"
            className="mb-3 rounded-md border border-amber-400 bg-amber-50 p-3 text-sm dark:border-amber-600 dark:bg-amber-950">
            <div className="mb-1 font-medium">Reason for deleting this incident (saved as a CANCELLED note)</div>
            <PermissionAwareTextarea aria-label="Delete reason" value={deleteReason} rows={2} onChange={(e) => setDeleteReason(e.target.value)} className={`${inputClass} mb-2`} />
            <div className="flex gap-2">
              <button type="button" onClick={handleDelete} disabled={saving || !deleteReason.trim()} aria-label="Confirm delete"
                className="rounded-md bg-red-600 px-3 py-1 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700">
                {saving ? 'Saving…' : 'Delete'}
              </button>
              <button type="button" onClick={() => { setDeletePrompt(false); setDeleteReason(''); }} className="rounded-md border border-rule px-3 py-1 text-sm hover:bg-highlight">Cancel</button>
            </div>
          </div>
        )}

        {loading && <div className="py-8 text-center text-sm text-muted">Loading incident…</div>}

        {!loading && detail && activeTab === 'incident' && (
          <IncidentInfoTab
            detail={detail}
            incident={incident}
            onChange={onIncidentChange}
            noshow={noshow}
            onNoshowChange={(k, v) => { setNoshow((p) => ({ ...p, [k]: v })); clearErrors(); }}
            isNew={isNew}
            canEdit={editable}
            portalContainer={portalContainer}
          />
        )}
        {!loading && detail && activeTab === 'parties' && (
          <PartiesTab rows={parties} onRowsChange={(rows) => { setParties(rows); clearErrors(); }} canEdit={editable}
            onAdd={() => setParties((p) => [...p, newPartyRow()])} />
        )}
        {!loading && detail && activeTab === 'equipment' && (
          <EquipmentTab rows={equipment} onRowsChange={(rows) => { setEquipment(rows); clearErrors(); }} canEdit={editable}
            onAdd={() => setEquipment((p) => [...p, newEquipmentRow()])} />
        )}
        {!loading && detail && activeTab === 'tickets' && recno !== null && (
          <TicketsTab recno={recno} tickets={detail.tickets ?? []} canEdit={editable} onChanged={refresh} />
        )}
        {!loading && detail && activeTab === 'claims' && (
          <ClaimsTab rows={claims} onRowsChange={(rows) => { setClaims(rows); clearErrors(); }} canEdit={editable} lookups={detail.lookups}
            onAdd={() => setClaims((p) => [...p, newClaimRow()])} />
        )}
        {!loading && detail && activeTab === 'costs' && (
          <CostsTab rows={costs} onRowsChange={(rows) => { setCosts(rows); clearErrors(); }} canEdit={editable} lookups={detail.lookups}
            claims={detail.claims ?? []} costlines={detail.costlines ?? []} incidentrecno={recno} onLinesSaved={refresh}
            onAdd={() => setCosts((p) => [...p, newCostRow()])} />
        )}
        {!loading && detail && activeTab === 'documents' && recno !== null && (
          <SafetyDocuments tablename="incident" tablerecno={recno} isReadOnly={!editable} />
        )}
        {!loading && detail && activeTab === 'notes' && recno !== null && (
          <NotesPanel
            fetchNotes={async () => ({ notes: (await safetySystemService.listNotes('incident', recno)).data ?? [] })}
            addNote={editable ? async (text) => { await safetySystemService.addNote('incident', recno, text); } : null}
            onNoteCountChange={setNoteCount}
            isReadOnly={!editable}
            currentUser={currentUser}
          />
        )}
        {!loading && detail && activeTab === 'history' && recno !== null && (
          <SafetyHistoryTab recno={recno} fetch={safetySystemService.getIncidentHistory} labels={INCIDENT_HISTORY_LABELS} emptyText="No history for this incident." />
        )}
      </div>
    </Drawer>
  );
}
